/**
 * Firestore Debug Service
 * 
 * This service tracks and analyzes Firestore operations to help identify
 * performance issues and excessive reads/writes.
 */

// Maximum number of log entries to keep
const MAX_LOG_ENTRIES = 1000;

// Operation types
export type OperationType = 'read' | 'write' | 'delete' | 'transaction' | 'batch';

// Log entry interface
export interface FirestoreLogEntry {
  timestamp: number;
  operation: OperationType;
  collection: string;
  docId?: string;
  component?: string;
  stackTrace?: string;
}

// Collection metrics interface
export interface CollectionMetrics {
  reads: number;
  writes: number;
  deletes: number;
  transactions: number;
  batches: number;
  total: number;
}

// Component metrics interface
export interface ComponentMetrics {
  reads: number;
  writes: number;
  deletes: number;
  transactions: number;
  batches: number;
  total: number;
}

class FirestoreDebugService {
  private logs: FirestoreLogEntry[] = [];
  private collectionMetrics: Map<string, CollectionMetrics> = new Map();
  private componentMetrics: Map<string, ComponentMetrics> = new Map();
  private startTime: number = Date.now();
  private enabled: boolean = true;
  
  // Minute-by-minute operation counts for charts
  private minutelyOperations: { timestamp: number; reads: number; writes: number; deletes: number; total: number }[] = [];
  private currentMinute: number = Math.floor(Date.now() / 60000);
  private currentMinuteReads: number = 0;
  private currentMinuteWrites: number = 0;
  private currentMinuteDeletes: number = 0;
  
  constructor() {
    // Set up interval to track minutely operations
    setInterval(() => this.updateMinutelyMetrics(), 60000);
  }
  
  /**
   * Log a Firestore operation
   */
  logOperation(operation: OperationType, collection: string, docId?: string): void {
    if (!this.enabled) return;
    
    // Skip expensive stack trace parsing for batch and delete operations to prevent UI freezing
    if (operation === 'batch' || operation === 'delete') {
      console.log(`[FirestoreDebug] ${operation} on ${collection}${docId ? `/${docId}` : ''} (lightweight logging)`);
      
      // Update metrics only, skip expensive operations
      this.updateCollectionMetrics(collection, operation);
      
      // Update current minute metrics based on operation type
      if (operation === 'delete') this.currentMinuteDeletes++;
      // batch operations don't count toward minute metrics
      return;
    }
    
    // Get component info from stack trace (only for non-batch/delete operations)
    const stackTrace = new Error().stack || '';
    const component = this.extractComponentFromStack(stackTrace);
    
    // Create log entry
    const entry: FirestoreLogEntry = {
      timestamp: Date.now(),
      operation,
      collection,
      docId,
      component,
      stackTrace
    };
    
    // Add to logs, keeping only the most recent entries
    this.logs.unshift(entry);
    if (this.logs.length > MAX_LOG_ENTRIES) {
      this.logs.pop();
    }
    
    // Update collection metrics
    this.updateCollectionMetrics(collection, operation);
    
    // Update component metrics
    if (component) {
      this.updateComponentMetrics(component, operation);
    }
    
    // Update current minute metrics
    if (operation === 'read') this.currentMinuteReads++;
    if (operation === 'write') this.currentMinuteWrites++;
    if (operation === 'transaction') {
      // Transactions can contain reads/writes, but we'll count them separately
      this.currentMinuteWrites++; // Count transactions as writes for simplicity
    }
  }
  
  /**
   * Get all log entries
   */
  getLogs(): FirestoreLogEntry[] {
    return [...this.logs];
  }
  
  /**
   * Get logs filtered by collection
   */
  getLogsByCollection(collection: string): FirestoreLogEntry[] {
    return this.logs.filter(log => log.collection === collection);
  }
  
  /**
   * Get logs filtered by operation type
   */
  getLogsByOperation(operation: OperationType): FirestoreLogEntry[] {
    return this.logs.filter(log => log.operation === operation);
  }
  
  /**
   * Get logs filtered by component
   */
  getLogsByComponent(component: string): FirestoreLogEntry[] {
    return this.logs.filter(log => log.component?.includes(component));
  }
  
  /**
   * Get collection metrics
   */
  getCollectionMetrics(): Map<string, CollectionMetrics> {
    return new Map(this.collectionMetrics);
  }
  
  /**
   * Get component metrics
   */
  getComponentMetrics(): Map<string, ComponentMetrics> {
    return new Map(this.componentMetrics);
  }
  
  /**
   * Get minutely operation metrics for charts
   */
  getMinutelyOperations(): { timestamp: number; reads: number; writes: number; deletes: number; total: number }[] {
    // Add current minute to the array
    this.updateMinutelyMetrics();
    return [...this.minutelyOperations];
  }
  
  /**
   * Get total operation counts
   */
  getTotalCounts(): { reads: number; writes: number; deletes: number; transactions: number; batches: number; total: number } {
    let reads = 0;
    let writes = 0;
    let deletes = 0;
    let transactions = 0;
    let batches = 0;
    
    for (const metrics of this.collectionMetrics.values()) {
      reads += metrics.reads;
      writes += metrics.writes;
      deletes += metrics.deletes;
      transactions += metrics.transactions;
      batches += metrics.batches;
    }
    
    return {
      reads,
      writes,
      deletes,
      transactions,
      batches,
      total: reads + writes + deletes + transactions + batches
    };
  }
  
  /**
   * Get operations per minute
   */
  getOperationsPerMinute(): { reads: number; writes: number; deletes: number; total: number } {
    const totalCounts = this.getTotalCounts();
    const minutesElapsed = Math.max(1, (Date.now() - this.startTime) / 60000);
    
    return {
      reads: Math.round(totalCounts.reads / minutesElapsed),
      writes: Math.round(totalCounts.writes / minutesElapsed),
      deletes: Math.round(totalCounts.deletes / minutesElapsed),
      total: Math.round(totalCounts.total / minutesElapsed)
    };
  }
  
  /**
   * Reset all metrics and logs
   */
  reset(): void {
    this.logs = [];
    this.collectionMetrics.clear();
    this.componentMetrics.clear();
    this.startTime = Date.now();
    this.minutelyOperations = [];
    this.currentMinute = Math.floor(Date.now() / 60000);
    this.currentMinuteReads = 0;
    this.currentMinuteWrites = 0;
    this.currentMinuteDeletes = 0;
  }
  
  /**
   * Enable or disable logging
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }
  
  /**
   * Check if logging is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }
  
  /**
   * Update collection metrics
   */
  private updateCollectionMetrics(collection: string, operation: OperationType): void {
    let metrics = this.collectionMetrics.get(collection);
    
    if (!metrics) {
      metrics = {
        reads: 0,
        writes: 0,
        deletes: 0,
        transactions: 0,
        batches: 0,
        total: 0
      };
      this.collectionMetrics.set(collection, metrics);
    }
    
    metrics[operation === 'read' ? 'reads' : 
            operation === 'write' ? 'writes' : 
            operation === 'delete' ? 'deletes' : 
            operation === 'transaction' ? 'transactions' : 'batches']++;
    metrics.total++;
  }
  
  /**
   * Update component metrics
   */
  private updateComponentMetrics(component: string, operation: OperationType): void {
    let metrics = this.componentMetrics.get(component);
    
    if (!metrics) {
      metrics = {
        reads: 0,
        writes: 0,
        deletes: 0,
        transactions: 0,
        batches: 0,
        total: 0
      };
      this.componentMetrics.set(component, metrics);
    }
    
    metrics[operation === 'read' ? 'reads' : 
            operation === 'write' ? 'writes' : 
            operation === 'delete' ? 'deletes' : 
            operation === 'transaction' ? 'transactions' : 'batches']++;
    metrics.total++;
  }
  
  /**
   * Update minutely metrics
   */
  private updateMinutelyMetrics(): void {
    const now = Date.now();
    const currentMinute = Math.floor(now / 60000);
    
    // If we've moved to a new minute, add the current minute to the array
    if (currentMinute > this.currentMinute) {
      this.minutelyOperations.push({
        timestamp: this.currentMinute * 60000,
        reads: this.currentMinuteReads,
        writes: this.currentMinuteWrites,
        deletes: this.currentMinuteDeletes,
        total: this.currentMinuteReads + this.currentMinuteWrites + this.currentMinuteDeletes
      });
      
      // Keep only the last 60 minutes
      if (this.minutelyOperations.length > 60) {
        this.minutelyOperations.shift();
      }
      
      // Reset for the new minute
      this.currentMinute = currentMinute;
      this.currentMinuteReads = 0;
      this.currentMinuteWrites = 0;
      this.currentMinuteDeletes = 0;
    }
  }
  
  /**
   * Extract component name from stack trace
   */
  private extractComponentFromStack(stackTrace: string): string {
    // Try to extract component name from stack trace
    const lines = stackTrace.split('\n');
    
    // Skip the first few lines which are from this service
    for (let i = 3; i < lines.length; i++) {
      const line = lines[i];
      
      // Look for component patterns in the stack trace
      if (line.includes('/components/')) {
        const match = line.match(/\/components\/([^/]+\/[^/]+)/);
        if (match) return match[1];
      }
      
      if (line.includes('/pages/')) {
        const match = line.match(/\/pages\/([^/]+\/[^/]+)/);
        if (match) return match[1];
      }
      
      if (line.includes('/services/')) {
        const match = line.match(/\/services\/([^/]+)/);
        if (match) return match[1];
      }
    }
    
    return 'unknown';
  }
}

// Create and export singleton instance
export const firestoreDebugService = new FirestoreDebugService();
